// src/pages/Login.tsx
import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { QrCode, Store as LucideStoreIcon, LayoutGrid } from 'lucide-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import { useScanner } from '../scanner/ScannerContextCore';
import { getBranding, ACCENT_CLASSES } from '../config/branding';

/**
 * Login-Komponente
 */
const Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Eingabezustände
  const [employeeId, setEmployeeId] = useState<string>(
    searchParams.get('employeeId') || ''
  );
  const [password, setPassword] = useState<string>(
    searchParams.get('password') || ''
  );
  // QR-Token als Konstante, da kein Setter benötigt wird
  const qrToken = searchParams.get('qr_token') || '';

  // Zustand für Fehler- und Ladeanzeige
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Globaler Kamera-Scanner
  const { scan: openScannerOverlay } = useScanner();

  // AuthStore-Zugriff
  const { login, setStores } = useAuthStore();

  // Hostname-basiertes Branding (Login-Überschrift, Icon, Akzentfarbe)
  const branding = getBranding();
  const accent = ACCENT_CLASSES[branding.accent];

  // Tab-Titel je nach Domain sprechend halten
  useEffect(() => {
    document.title =
      branding.key === 'layout'
        ? 'Layoutdesigner · Store Portal'
        : 'Store Portal';
  }, [branding.key]);

  const inputClass = `block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 ${accent.input}`;
  const primaryButtonClass = `inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed ${accent.button}`;
  const secondaryButtonClass =
    'inline-flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50';

  /**
   * Beispiel-Funktion: Lädt Filialen nach dem Login.
   */
  const fetchStores = async () => {
    try {
      const response = await fetch('/api/stores/user', {
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error('Fehler beim Laden der Märkte');
      }
      const storesData = await response.json();
      setStores(storesData);
    } catch (err) {
      console.error('Error fetching stores:', err);
    }
  };

  /**
   * Auto-Login per URL-Parameter (employeeId + password/qr_token).
   */
  useEffect(() => {
    const urlEmployeeId = searchParams.get('employeeId');
    const urlPassword = searchParams.get('password');
    const urlQrToken = searchParams.get('qr_token');

    if (urlEmployeeId && (urlPassword || urlQrToken)) {
      handleLogin(null, urlEmployeeId, urlPassword || '', urlQrToken || '');
      navigate('/', { replace: true });
    }
  }, [searchParams, navigate]);

  /**
   * handleLogin: Führt die Anmeldung aus.
   */
  const handleLogin = async (
    e: React.FormEvent<HTMLFormElement> | null,
    autoEmployeeId?: string,
    autoPassword?: string,
    autoQrToken?: string
  ) => {
    if (e) e.preventDefault();
    setError('');
    setIsLoading(true);

    const loginEmployeeId = autoEmployeeId || employeeId;
    const loginPassword = autoPassword || password;
    const loginQrToken = autoQrToken || qrToken;

    try {
      await login({
        employeeId: loginEmployeeId,
        password: loginPassword || undefined,
        qr_token: loginQrToken || undefined
      });

      await fetchStores();
      navigate('/');
    } catch (err) {
      if (err instanceof Error) {
        setError('Unbekannter Fehler');
      } else {
        setError('Login fehlgeschlagen');
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * handleScanSuccess: Wird aufgerufen, wenn der QR-Code erfolgreich gescannt wurde.
   */
  const handleScanSuccess = (decodedText: string): void => {
    try {
      const url = new URL(decodedText);
      const scannedEmployeeId = url.searchParams.get('employeeId');
      const scannedPassword = url.searchParams.get('password');
      const scannedQrToken = url.searchParams.get('qr_token');

      if (scannedEmployeeId) {
        if (scannedQrToken) {
          handleLogin(null, scannedEmployeeId, '', scannedQrToken);
        } else if (scannedPassword) {
          handleLogin(null, scannedEmployeeId, scannedPassword, '');
        } else {
          setError('Ungültige Anmeldedaten im QR-Code');
        }
      } else {
        setError('Fehlerhafte URL im QR-Code');
      }
    } catch (error) {
      setError('Fehler beim Verarbeiten des QR-Codes');
    }
  };

  /**
   * handleQrLogin: Öffnet den globalen Scanner; fremde QR-Codes werden im
   * Overlay gemeldet und es wird weitergescannt.
   */
  const handleQrLogin = async () => {
    setError('');
    const result = await openScannerOverlay({
      title: 'QR-Code anmelden',
      hint: 'Anmelde-QR-Code in den Rahmen halten',
      formats: ['QRCode'],
      expect: ['qr'],
      validate: (r) => {
        try {
          const url = new URL(r.rawText);
          return url.searchParams.get('employeeId') ? true : 'Fehlerhafte URL im QR-Code';
        } catch {
          return 'Kein gültiger Anmelde-QR-Code';
        }
      },
    });
    if (result) handleScanSuccess(result.rawText);
  };

  /**
   * JSX-UI
   */
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col justify-center px-4 py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="surface-icon-tile" style={{ width: 56, height: 56 }}>
            {branding.loginIcon === 'layout' ? (
              <LayoutGrid className={`h-7 w-7 ${accent.tile}`} />
            ) : (
              <LucideStoreIcon className={`h-7 w-7 ${accent.tile}`} />
            )}
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-slate-900">
          {branding.loginHeading}
        </h2>
      </div>
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="surface surface-section-lg">
          <form className="space-y-6" onSubmit={handleLogin}>
              <div>
                <label
                  htmlFor="employeeId"
                  className="block text-sm font-medium text-gray-700"
                >
                  Benutzerkennung
                </label>
                <div className="mt-1">
                  <input
                    id="employeeId"
                    name="employeeId"
                    type="text"
                    autoComplete="username"
                    required
                    value={employeeId}
                    onChange={(e) => setEmployeeId(e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700"
                >
                  Passwort
                </label>
                <div className="mt-1">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>
              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              )}
              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={primaryButtonClass}
                >
                  {isLoading ? 'Anmeldung...' : 'Anmelden'}
                </button>
              </div>
              {isMobile && (
                <button
                  type="button"
                  onClick={() => void handleQrLogin()}
                  className={secondaryButtonClass}
                >
                  <QrCode className="h-4 w-4" />
                  QR-Code scannen
                </button>
              )}
            </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
